<template>
  <div>
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-info header-enhanced">
      <!-- Music Albums Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Albums"
            type="gradient-info"
            :sub-title="stats.totalAlbums ? stats.totalAlbums.toLocaleString() : 'Loading...'"
            icon="ni ni-music-note"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">↗ {{ stats.albumGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Songs"
            type="gradient-success"
            :sub-title="stats.totalSongs ? stats.totalSongs.toLocaleString() : 'Loading...'"
            icon="ni ni-note-03"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">🎵 Tracks</span>
              <span class="text-nowrap">Across all albums</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Avg Songs/Album"
            type="gradient-warning"
            :sub-title="stats.avgSongsPerAlbum ? stats.avgSongsPerAlbum.toFixed(1) : 'Loading...'"
            icon="ni ni-chart-bar-32"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">📊 Average</span>
              <span class="text-nowrap">Per album</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Most Popular"
            type="gradient-purple"
            :sub-title="stats.mostPopularAlbum ? stats.mostPopularAlbum : 'Loading...'"
            icon="ni ni-trophy"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">🏆 Top</span>
              <span class="text-nowrap">Most played</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-music-note mr-2"></i>Music Albums Management
          </h3>
          <p class="text-white-50 mb-0">Manage and organize your music albums</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button variant="light" size="sm" class="shadow-sm">
            <i class="ni ni-fat-add mr-1"></i>Create Album
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <b-row>
        <b-col cols="12" lg="8" class="pr-lg-2">
          <div class="d-flex flex-wrap justify-content-center album-list-container">
            <b-card
              v-for="alb in albums"
              :key="alb.id"
              @click="selectedAlbum(alb)"
              class="album-card m-2 p-0"
              :class="{ 'selected-card': album && album.id === alb.id }"
              no-body
            >
              <b-card-img-lazy :src="alb.thumb" :alt="alb.albumName" class="card-img-top"></b-card-img-lazy>
              <b-card-body class="p-2 text-center">
                <b-card-title class="mb-1 card-title-text">{{ alb.albumName }}</b-card-title>
                <b-card-text class="card-order-text">
                  Order: <b>{{ alb.orderNo }}</b>
                </b-card-text>
              </b-card-body>
            </b-card>
            <div v-if="albums.length === 0" class="text-center w-100 py-5">
              <p>No albums available.</p>
              <p class="text-muted">Loading or no albums found.</p>
            </div>
          </div>
        </b-col>

        <b-col cols="12" lg="4" class="pt-2 pl-0 mt-4 mt-lg-0 sidebar-column col-lg-4 col-12">
          <div class="album-details-sidebar">
            <h4 class="sidebar-title mb-3">Album Details</h4>
            <div v-if="album">
              <album-card
                :albumId="album.id"
                @update-selected-album="updateSelectedAlbum"
                @delete-album-update="deleteAlbumUpdate"
                :albumTotal="albums.length"
                class="fixed-album-card"
              >
              </album-card>
            </div>
            <div v-else class="text-center py-5 no-album-selected">
              <p class="text-muted">Select an album to view its details.</p>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
  const AlbumCard = () => import('./AlbumCard.vue');
  import { mapActions } from 'vuex';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';

  // Import BootstrapVue components needed for the layout
  import { BRow, BCol, BCard, BCardImgLazy, BCardBody, BCardTitle, BCardText } from 'bootstrap-vue';

  export default {
    name : 'AlbumList',
    data() {
      return {
        album  : null,
        albums : [],
        stats: {
          totalAlbums: null,
          totalSongs: null,
          avgSongsPerAlbum: null,
          mostPopularAlbum: null,
          albumGrowth: 0,
        },
      }
    },
    components : {
      AlbumCard,
      baseHeader,
      StatsCard,
      // Register BootstrapVue components
      BRow,
      BCol,
      BCard,
      BCardImgLazy,
      BCardBody,
      BCardTitle,
      BCardText
    },
    methods : {
      ...mapActions({
        'getAlbumSongs'   : 'media/getAlbumSongs',
        'clearAlbumSongs' : 'media/clearAlbumSongs',
      }),

      /**
       * Get Album List
       */
      getAlbums() {
        this.$http.get('/api/albums', {
          params : {
            page    : 1,
            perPage : 500,
            sort    : '',
          },
        }).then(res => {
          this.albums = [];
          const alb = res.data.data;
          for (let i=0; i < alb.length; i++) {
            this.albums.push({
              id        : alb[i].item.mobile_album_id,
              albumName : alb[i].item.album_name,
              thumb     : alb[i].item.album_thumbnail,
              orderNo   : alb[i].item.order_no,
            });
          }
          // If no album is selected, select the first one by default
          // This will ensure the sidebar is not empty on load
          if (!this.album && this.albums.length > 0) {
            this.selectedAlbum(this.albums[0]);
          }
        }).catch(error => {
          console.error("Error fetching albums:", error);
          this.albums = []; // Ensure the list is empty if there's an error
        });
      },

      /**
       * Selected album
       *
       * @param data
       */
      selectedAlbum(data) {
        this.album = data;
        this.clearAlbumSongs();
        this.getAlbumSongs(data.id);
      },

      /**
       * Update Selected Album Data
       *
       * @param album
       */
      updateSelectedAlbum(album) {
        // Reload the list to ensure the most recent data
        this.getAlbums().then(() => {
          // Find the updated album in the new list to ensure reactivity
          const updatedAlbumInList = this.albums.find(a => a.id === album.id);
          if (updatedAlbumInList) {
            this.selectedAlbum(updatedAlbumInList);
          } else {
            // If the album is no longer in the list (e.g., was deleted), deselect it
            this.album = null;
          }
        });
      },

      /**
       * Update Album List after Update
       */
      deleteAlbumUpdate() {
        this.getAlbums();
        this.album = null; // Deselect the album if the current one is deleted
      },

      /**
       * Initialize Album Statistics
       */
      initializeStats() {
        // Simulated album statistics - replace with actual API calls
        this.stats.totalAlbums = this.albums.length;
        this.stats.totalSongs = 0; // This would be calculated from album songs
        this.stats.avgSongsPerAlbum = 0; // This would be calculated
        this.stats.mostPopularAlbum = 'Summer Vibes'; // This would be from analytics
        this.stats.albumGrowth = 15.7;
      },
    },
    mounted() {
      this.getAlbums();
      // Initialize stats after albums are loaded
      this.$nextTick(() => {
        this.initializeStats();
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/components/admin/mobile_music/album-list";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(23, 162, 184, 0.9) 0%, rgba(0, 123, 191, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(0, 123, 191, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(0, 123, 191, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

  /* Enhanced Album List Container */
  .album-list-container {
    overflow-y: auto;
    padding-right: 15px;
    padding-bottom: 20px;

    /* Custom scrollbar styles */
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: rgba(248, 249, 250, 0.8);
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(23, 162, 184, 0.6);
      border-radius: 10px;
      transition: all 0.3s ease;
      &:hover {
        background: rgba(23, 162, 184, 0.8);
      }
    }
    scrollbar-width: thin;
    scrollbar-color: rgba(23, 162, 184, 0.6) rgba(248, 249, 250, 0.8);
  }

  /* Enhanced Album Cards */
  .album-card {
    width: calc(33.3333% - 16px);
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
    border-radius: 12px !important;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
    background: white;
    position: relative;
    animation: fadeInUp 0.6s ease-out;
    animation-fill-mode: both;
  }

  .album-card:nth-child(1) { animation-delay: 0.1s; }
  .album-card:nth-child(2) { animation-delay: 0.2s; }
  .album-card:nth-child(3) { animation-delay: 0.3s; }
  .album-card:nth-child(4) { animation-delay: 0.4s; }
  .album-card:nth-child(5) { animation-delay: 0.5s; }
  .album-card:nth-child(6) { animation-delay: 0.6s; }

  .album-card:hover {
    border-color: rgba(23, 162, 184, 0.8);
    box-shadow: 0 8px 25px rgba(23, 162, 184, 0.15);
    transform: translateY(-5px) scale(1.02);
  }

  .album-card.selected-card {
    border-color: rgba(23, 162, 184, 1);
    box-shadow: 0 8px 30px rgba(23, 162, 184, 0.25);
    transform: translateY(-5px) scale(1.02);
  }

  .album-card.selected-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #17a2b8, #138496);
    border-radius: 12px 12px 0 0;
  }

  .card-img-top {
    width: 100%;
    height: 140px;
    object-fit: cover;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    border-bottom: 1px solid rgba(23, 162, 184, 0.1);
    transition: all 0.3s ease;
  }

  .album-card:hover .card-img-top {
    transform: scale(1.05);
  }

  .card-title-text {
    font-size: 0.95rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #344767;
    margin-bottom: 0.25rem;
  }

  .card-order-text {
    font-size: 0.8rem;
    color: #8898aa;
    font-weight: 500;
  }

  .card-body {
    padding: 12px 16px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 249, 250, 0.9) 100%);
  }

  /* Enhanced Empty State */
  .text-center.py-5 {
    padding: 3rem 1rem !important;
    background: rgba(248, 249, 250, 0.5);
    border-radius: 12px;
    border: 2px dashed rgba(23, 162, 184, 0.3);
  }

  .text-center.py-5 p {
    color: #6c757d;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }

  .text-center.py-5 .text-muted {
    color: #adb5bd;
  }

  /* Enhanced Sidebar Column */
  .sidebar-column {
    position: sticky;
    top: 100px;
    align-self: flex-start;
    max-height: calc(100vh - 100px);
    overflow-y: auto;

    /* Custom scrollbar styles */
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-track {
      background: rgba(248, 249, 250, 0.8);
      border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(23, 162, 184, 0.6);
      border-radius: 10px;
      transition: all 0.3s ease;
      &:hover {
        background: rgba(23, 162, 184, 0.8);
      }
    }
    scrollbar-width: thin;
    scrollbar-color: rgba(23, 162, 184, 0.6) rgba(248, 249, 250, 0.8);
  }

  /* Enhanced Album Details Sidebar */
  .album-details-sidebar {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%);
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    border: 1px solid rgba(23, 162, 184, 0.1);
    position: relative;
    transition: all 0.3s ease;
    animation: slideInRight 0.8s ease-out;
  }

  .album-details-sidebar:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  .album-details-sidebar::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #17a2b8, #138496);
    border-radius: 12px 12px 0 0;
  }

  /* Enhanced Sidebar Title */
  .sidebar-title {
    color: #344767;
    font-size: 1.1rem;
    font-weight: 700;
    padding-bottom: 1rem;
    border-bottom: 2px solid rgba(23, 162, 184, 0.2);
    margin-bottom: 1.5rem !important;
    text-align: center;
    position: relative;
  }

  .sidebar-title::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 2px;
    background: linear-gradient(90deg, #17a2b8, #138496);
    border-radius: 1px;
  }

  /* Enhanced No Album Selected State */
  .no-album-selected {
    color: #8898aa;
    font-style: italic;
    padding: 3rem 1.5rem;
    background: linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(255, 255, 255, 0.8) 100%);
    border-radius: 8px;
    border: 2px dashed rgba(23, 162, 184, 0.3);
    margin-top: 1.5rem;
    text-align: center;
    transition: all 0.3s ease;
  }

  .no-album-selected:hover {
    background: linear-gradient(135deg, rgba(23, 162, 184, 0.05) 0%, rgba(23, 162, 184, 0.02) 100%);
    border-color: rgba(23, 162, 184, 0.5);
  }

  .no-album-selected p {
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  /* Adjustments for AlbumCard within the sidebar */
  .fixed-album-card {
    width: 100%; /* Ensures AlbumCard takes full available width */
    // Consider adding max-width or max-height if AlbumCard can be very large
  }

  table {
    z-index: 99;
    position: relative;
  }

  .header {
  margin-top: -80px;
}
  

  /* Animations */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Responsive Design */
  @media (max-width: 991.98px) {
    .sidebar-column {
      position: relative;
      top: 0;
      max-height: none;
      overflow-y: visible;
      border-left: none;
      border-top: 1px solid rgba(23, 162, 184, 0.2);
      margin-top: 2rem !important;
      padding: 1rem;
      border-radius: 0;
      background-color: transparent;
      box-shadow: none;
    }

    .album-details-sidebar {
      margin: 0;
      padding: 1rem;
      border-radius: 8px;
    }

    .sidebar-title {
      font-size: 1.1rem;
      padding-bottom: 0.75rem;
      margin-bottom: 1rem !important;
    }

    .album-card {
      width: calc(50% - 16px);
      margin-bottom: 1rem;
    }

    .album-list-container {
      padding-right: 0;
    }
  }

  @media (max-width: 576px) {
    .album-card {
      width: calc(100% - 16px);
    }

    .stats-card-hover {
      margin-bottom: 1rem;
    }

    .header-enhanced .card {
      margin-bottom: 1rem;
    }
  }
</style>